import {
  isWarshipType,
  ORBITAL_BASE_MASS,
  ORDNANCE_MASS,
  SHIP_STATS,
} from '../constants';
import { type HexKey, hexEqual, parseHexKey } from '../hex';
import { bodyHasGravity } from '../map-data';
import { computeCourse, detectOrbit } from '../movement';
import { deriveCapabilities } from '../scenario-capabilities';
import {
  type EngineError,
  ErrorCode,
  type GameState,
  type Ordnance,
  PHASE_TRANSITIONS,
  type Phase,
  type PhaseSuccessor,
  type PlayerId,
  type Ship,
  type SolarSystemMap,
} from '../types';
import type { EngineEvent } from './engine-events';

/**
 * Transition the game to a new phase, validating against the phase transition table.
 * Throws in development if the transition is not listed in PHASE_TRANSITIONS.
 */
export const transitionPhase = <P extends Phase>(
  state: GameState & { phase: P },
  next: PhaseSuccessor<P>,
): void => {
  const allowed = PHASE_TRANSITIONS[state.phase] as readonly Phase[];
  if (!allowed.includes(next)) {
    throw new Error(`Invalid phase transition: ${state.phase} → ${next}`);
  }
  (state as GameState).phase = next;
};

export const transitionPhaseWithEvent = (
  state: GameState,
  next: Phase,
  engineEvents?: EngineEvent[],
): void => {
  transitionPhase(state as GameState & { phase: Phase }, next as never);
  engineEvents?.push({
    type: 'phaseChanged',
    phase: state.phase,
    turn: state.turnNumber,
    activePlayer: state.activePlayer,
  });
};

export const setGameOutcome = (
  state: GameState,
  winner: PlayerId,
  reason: string,
  engineEvents?: EngineEvent[],
): void => {
  transitionPhase(state as GameState & { phase: Phase }, 'gameOver' as never);
  state.outcome = { winner, reason };
  engineEvents?.push({
    type: 'gameOver',
    winner,
    reason,
  });
};

// Phase + player validation for engine entry points.
// Returns an error string if the action is not allowed,
// or null if validation passes.
export const validatePhaseAction = (
  state: GameState,
  playerId: PlayerId,
  requiredPhase: Phase,
): EngineError | null => {
  if (state.phase !== requiredPhase) {
    return {
      code: ErrorCode.INVALID_PHASE,
      message: `Not in ${requiredPhase} phase`,
    };
  }

  if (playerId !== state.activePlayer) {
    return {
      code: ErrorCode.NOT_YOUR_TURN,
      message: 'Not your turn',
    };
  }
  return null;
};

export const engineError = (code: ErrorCode, message: string): EngineError => ({
  code,
  message,
});

export const engineFailure = (code: ErrorCode, message: string) => ({
  error: engineError(code, message),
});

export const playerControlsBase = (
  state: GameState,
  playerId: PlayerId,
  baseKey: HexKey,
): boolean => state.players[playerId]?.bases.includes(baseKey) ?? false;

export { deriveCapabilities } from '../scenario-capabilities';

export const isPlanetaryDefenseEnabled = (
  state: Pick<GameState, 'scenarioRules'>,
): boolean => deriveCapabilities(state.scenarioRules).planetaryDefenseEnabled;

export const usesEscapeInspectionRules = (
  state: Pick<GameState, 'scenarioRules'>,
): boolean => deriveCapabilities(state.scenarioRules).hiddenIdentityInspection;

export const getEscapeEdge = (
  state: Pick<GameState, 'scenarioRules'>,
): 'any' | 'north' => deriveCapabilities(state.scenarioRules).escapeEdge;

export { parseHexKey as parseBaseKey } from '../hex';

export const getOwnedPlanetaryBases = (
  state: GameState,
  playerId: PlayerId,
  map: SolarSystemMap,
): { key: string; coord: { q: number; r: number } }[] => {
  const bases = state.players[playerId]?.bases ?? [];

  return bases.flatMap((key) => {
    if (state.destroyedBases.includes(key)) return [];

    const hex = map.hexes.get(key);

    if (!hex?.base || !bodyHasGravity(hex.base.bodyName, map)) {
      return [];
    }

    return [{ key, coord: parseHexKey(key) }];
  });
};

export const getAllowedOrdnanceTypes = (
  state: Pick<GameState, 'scenarioRules'>,
): Set<Ordnance['type']> =>
  new Set(deriveCapabilities(state.scenarioRules).allowedOrdnanceTypes);

export const getCargoUsedAfterResupply = (
  ship: Pick<Ship, 'baseStatus'>,
): number => (ship.baseStatus === 'carryingBase' ? ORBITAL_BASE_MASS : 0);

export const RESUPPLY_ORDNANCE_ERROR =
  'Ships cannot launch ordnance during a turn in which they resupply';

export const getNextOrdnanceId = (state: Pick<GameState, 'ordnance'>): number =>
  state.ordnance.reduce((maxId, ord) => {
    const match = /^ord(\d+)$/.exec(ord.id);
    return match ? Math.max(maxId, Number(match[1]) + 1) : maxId;
  }, 0);

export const hasOrdnanceCapacity = (ship: Ship): boolean => {
  const stats = SHIP_STATS[ship.type];

  if (!stats) return false;

  const minMass = ORDNANCE_MASS.mine;

  return stats.cargo - ship.cargoUsed >= minMass;
};

// Ship-level eligibility to launch a specific ordnance type.
// Returns an error message or null if the launch is allowed.
// Does NOT check scenario allowed types, mine committed-burn rule,
// or resupply turn — see validateOrdnanceLaunch.
export const validateShipOrdnanceLaunch = (
  ship: Ship,
  ordnanceType: Ordnance['type'],
): EngineError | null => {
  const stats = SHIP_STATS[ship.type];

  if (!stats) return engineError(ErrorCode.INVALID_INPUT, 'Unknown ship type');

  if (ship.lifecycle === 'destroyed') {
    return engineError(ErrorCode.STATE_CONFLICT, 'Ship is destroyed');
  }

  if (ship.lifecycle === 'landed') {
    return engineError(
      ErrorCode.STATE_CONFLICT,
      'Cannot launch ordnance while landed',
    );
  }

  if (ship.control === 'captured')
    return engineError(
      ErrorCode.NOT_ALLOWED,
      'Captured ships cannot launch ordnance',
    );

  // Orbital bases may launch at D1 damage; dreadnaughts operate at any damage (rulebook p.6)
  if (ship.damage.disabledTurns > 0) {
    if (
      !stats.operatesWhileDisabled &&
      !(stats.operatesAtD1 && ship.damage.disabledTurns <= 1)
    ) {
      return engineError(ErrorCode.STATE_CONFLICT, 'Ship is disabled');
    }
  }

  if (ship.type === 'orbitalBase' && ordnanceType !== 'torpedo') {
    return engineError(
      ErrorCode.NOT_ALLOWED,
      'Orbital bases can only launch torpedoes',
    );
  }

  if (ordnanceType === 'torpedo' && !stats.canLaunchTorpedoes) {
    return engineError(
      ErrorCode.NOT_ALLOWED,
      'Only warships and orbital bases can launch torpedoes',
    );
  }

  if (
    ordnanceType === 'nuke' &&
    !isWarshipType(ship.type) &&
    ship.nukesLaunchedSinceResupply >= 1
  ) {
    return engineError(
      ErrorCode.RESOURCE_LIMIT,
      'Non-warships may launch only one nuke between resupplies',
    );
  }

  const mass = ORDNANCE_MASS[ordnanceType];

  if (mass == null) {
    return engineError(ErrorCode.INVALID_INPUT, 'Invalid ordnance type');
  }

  if (ship.cargoUsed + mass > stats.cargo) {
    const free = stats.cargo - ship.cargoUsed;
    return engineError(
      ErrorCode.RESOURCE_LIMIT,
      `Not enough cargo (need ${mass}, have ${free})`,
    );
  }

  return null;
};

/** State slice required to validate mine launches against resolved movement. */
export type OrdnanceLaunchValidationState = Pick<
  GameState,
  'scenarioRules' | 'pendingAstrogationOrders' | 'players' | 'destroyedBases'
>;

const mineResolvedMovementLeavesLaunchHex = (
  state: Pick<
    GameState,
    'pendingAstrogationOrders' | 'players' | 'destroyedBases'
  >,
  ship: Ship,
  map: SolarSystemMap,
): boolean => {
  const pendingOrder = (state.pendingAstrogationOrders ?? []).find(
    (o) => o.shipId === ship.id,
  );
  const isDisabled = ship.damage.disabledTurns > 0;
  const burn = isDisabled ? null : (pendingOrder?.burn ?? null);
  const overload = isDisabled ? null : (pendingOrder?.overload ?? null);

  const targetBody = state.players[ship.owner]?.targetBody;
  const shouldAutoLand =
    !pendingOrder?.land &&
    Boolean(targetBody) &&
    detectOrbit(ship, map) === targetBody;

  const course = computeCourse(ship, burn, map, {
    overload,
    weakGravityChoices: pendingOrder?.weakGravityChoices,
    destroyedBases: state.destroyedBases,
    land: pendingOrder?.land || shouldAutoLand || undefined,
  });

  return !hexEqual(course.destination, ship.position);
};

// Full launch validation including scenario restrictions
// and turn-level ship constraints (including mine + committed course change).
export const validateOrdnanceLaunch = (
  state: OrdnanceLaunchValidationState,
  ship: Ship,
  ordnanceType: Ordnance['type'],
  map?: SolarSystemMap | null,
): EngineError | null => {
  const allowedTypes = getAllowedOrdnanceTypes(state);

  if (!allowedTypes.has(ordnanceType)) {
    return engineError(
      ErrorCode.NOT_ALLOWED,
      `This scenario does not allow ${ordnanceType} launches`,
    );
  }

  if (ship.resuppliedThisTurn) {
    return engineError(ErrorCode.NOT_ALLOWED, RESUPPLY_ORDNANCE_ERROR);
  }

  const shipError = validateShipOrdnanceLaunch(ship, ordnanceType);

  if (shipError) {
    return shipError;
  }

  if (ordnanceType === 'mine') {
    const pendingOrder = (state.pendingAstrogationOrders ?? []).find(
      (o) => o.shipId === ship.id,
    );
    const isDisabled = ship.damage.disabledTurns > 0;
    const burn = isDisabled ? null : (pendingOrder?.burn ?? null);
    const hasCommittedBurn = burn != null && ship.fuel > 0;

    if (!hasCommittedBurn) {
      return engineError(
        ErrorCode.NOT_ALLOWED,
        'Ship must change course when launching a mine',
      );
    }

    if (map && !mineResolvedMovementLeavesLaunchHex(state, ship, map)) {
      return engineError(
        ErrorCode.NOT_ALLOWED,
        'Committed mine launch course must leave this hex',
      );
    }
  }

  return null;
};

export const hasValidOrdnanceLaunch = (
  state: OrdnanceLaunchValidationState,
  ship: Ship,
  allowedTypes = getAllowedOrdnanceTypes(state),
  map?: SolarSystemMap | null,
): boolean => {
  for (const ordnanceType of allowedTypes) {
    if (validateOrdnanceLaunch(state, ship, ordnanceType, map) === null) {
      return true;
    }
  }

  return false;
};

// Quick boolean: can this ship launch any ordnance at all?
// Checks ship status and minimum cargo capacity.
export const canLaunchOrdnance = (ship: Ship): boolean => {
  if (ship.lifecycle !== 'active' || ship.control === 'captured') return false;

  // Orbital bases may launch at D1 damage (rulebook p.6)
  if (ship.damage.disabledTurns > 0) {
    const stats = SHIP_STATS[ship.type];
    if (!stats.operatesAtD1 || ship.damage.disabledTurns > 1) {
      return false;
    }
  }

  return hasOrdnanceCapacity(ship);
};

export const hasLaunchableOrdnanceCapacity = (
  ship: Ship,
  allowedTypes: Set<Ordnance['type']>,
): boolean => {
  if (!canLaunchOrdnance(ship)) return false;

  for (const ordnanceType of allowedTypes) {
    if (validateShipOrdnanceLaunch(ship, ordnanceType) === null) {
      return true;
    }
  }

  return false;
};

export const isOrderableShip = (ship: Ship): boolean =>
  ship.lifecycle !== 'destroyed' &&
  ship.baseStatus !== 'emplaced' &&
  ship.control !== 'captured';

export const getOrderableShipsForPlayer = (
  state: Pick<GameState, 'ships'>,
  playerId: PlayerId,
): Ship[] =>
  state.ships.filter(
    (ship) => ship.owner === playerId && isOrderableShip(ship),
  );

export const hasAnyEnemyShips = (state: GameState): boolean => {
  const { activePlayer } = state;

  return state.ships.some(
    (s) => s.owner !== activePlayer && s.lifecycle !== 'destroyed',
  );
};

export const shuffle = <T>(items: T[], rng: () => number): T[] => {
  const copy = [...items];

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
};

// Ships drifting this far beyond the map bounds are destroyed by the
// movement resolver. Escape victories share the same threshold: the escape
// margin must never exceed the destruction margin, or an escaping ship is
// destroyed (and the win inverted) before the escape check can ever see it.
export const OOB_DESTRUCTION_MARGIN = 2;

export const hasEscaped = (
  pos: { q: number; r: number },
  bounds: {
    minQ: number;
    maxQ: number;
    minR: number;
    maxR: number;
  },
): boolean => {
  const margin = OOB_DESTRUCTION_MARGIN;

  return (
    pos.q < bounds.minQ - margin ||
    pos.q > bounds.maxQ + margin ||
    pos.r < bounds.minR - margin ||
    pos.r > bounds.maxR + margin
  );
};

// Apply a disconnect-forfeit game-over transition to a game state.
// Returns a new (cloned) state and the engine event for replay parity.
export const applyDisconnectForfeit = (
  inputState: GameState,
  disconnectedPlayer: PlayerId,
): { state: GameState; events: EngineEvent[] } => {
  const state = structuredClone(inputState);
  const winner = (disconnectedPlayer === 0 ? 1 : 0) as PlayerId;
  const reason = 'Opponent disconnected';
  setGameOutcome(state, winner, reason);
  return {
    state,
    events: [{ type: 'gameOver' as const, winner, reason }],
  };
};

export const hasEscapedNorth = (
  pos: { q: number; r: number },
  bounds: {
    minQ: number;
    maxQ: number;
    minR: number;
    maxR: number;
  },
): boolean => {
  const margin = OOB_DESTRUCTION_MARGIN;

  return pos.r < bounds.minR - margin;
};
